/**
 * MODULE: _shared/frontend-shell/src/features/crm/hooks/useCrmTasks.ts
 * PURPOSE: CRM tasks from /api/v1/crm/tasks — optimistic mutations with rollback
 * DEPENDENCIES: react, @shared/lib/frontend/api/crmApi, ./crmEntityMappers, ../types/task.types
 * PUBLIC_API: useCrmTasks
 * SIDE_EFFECTS: HTTP
 * AGENT_SAFETY: SAFE
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { crmCreate, crmDelete, crmList, crmPatch } from '@shared/lib/frontend/api/crmApi';
import type { Task } from '../types/task.types';
import { CRM_UNKNOWN_USER_LABEL, crmTaskToUpdateRequest, mapTaskApiToCrm } from './crmEntityMappers';

export type UseCrmTasksMutate = {
  addTask: (task: Omit<Task, 'id' | 'createdAtISO' | 'updatedAtISO'> & { id?: string }) => Promise<void>;
  patchTask: (id: string, patch: Partial<Task>) => Promise<void>;
  removeTask: (id: string) => Promise<void>;
};

export type UseCrmTasksResult = {
  data: Task[];
  isLoading: boolean;
  error: string | null;
  mutate: UseCrmTasksMutate;
  refetch: () => Promise<void>;
};

function taskToCrmCreateBody(
  task: Omit<Task, 'id' | 'createdAtISO' | 'updatedAtISO'> & { id?: string }
): Record<string, unknown> {
  return {
    title: task.title,
    type: task.type ?? 'task',
    relatedEntityId: task.relatedEntityId,
    relatedEntityType: task.relatedEntityType,
    ownerId: task.ownerId,
    dueAt: task.dueDateISO,
    status:
      task.status === 'done'
        ? 'done'
        : task.status === 'in_progress'
          ? 'in_progress'
          : task.status === 'overdue'
            ? 'todo'
            : 'todo',
    priority: task.priority ?? 'medium',
    notes: task.description ?? task.notes,
  };
}

function taskPatchToCrmBody(patch: Partial<Task>): Record<string, unknown> {
  const u = crmTaskToUpdateRequest(patch) as Record<string, unknown>;
  const out: Record<string, unknown> = { ...u };
  if (patch.dueDateISO !== undefined) out.dueAt = patch.dueDateISO;
  if (patch.relatedEntityId !== undefined) out.relatedEntityId = patch.relatedEntityId;
  if (patch.relatedEntityType !== undefined) out.relatedEntityType = patch.relatedEntityType;
  if (patch.ownerId !== undefined) out.ownerId = patch.ownerId;
  return out;
}

export function useCrmTasks(): UseCrmTasksResult {
  const [data, setData] = useState<Task[]>([]);
  const dataRef = useRef<Task[]>([]);
  dataRef.current = data;
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await crmList<Record<string, unknown>>('tasks', { limit: 200 });
      const rows = Array.isArray(res.items) ? res.items : [];
      setData(rows.map((r) => mapTaskApiToCrm(r)));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  const patchTask = useCallback(async (id: string, patch: Partial<Task>) => {
    const prev = structuredClone(dataRef.current);
    setData((prevRows) =>
      prevRows.map((t) => (t.id === id ? { ...t, ...patch, updatedAtISO: new Date().toISOString() } : t))
    );
    try {
      await crmPatch('tasks', id, taskPatchToCrmBody(patch));
    } catch (e) {
      setData(prev);
      throw e;
    }
  }, []);

  const addTask = useCallback(async (task: Omit<Task, 'id' | 'createdAtISO' | 'updatedAtISO'> & { id?: string }) => {
    const prev = structuredClone(dataRef.current);
    const now = new Date().toISOString();
    const tempId = task.id ?? `temp-${Date.now()}`;
    const optimistic: Task = {
      ...task,
      id: tempId,
      type: task.type ?? 'task',
      status: task.status ?? 'todo',
      priority: task.priority ?? 'medium',
      ownerId: task.ownerId ?? '',
      ownerName: task.ownerName?.trim() ? task.ownerName : CRM_UNKNOWN_USER_LABEL,
      dueDateISO: task.dueDateISO,
      createdAtISO: now,
      updatedAtISO: now,
    } as Task;
    setData((rows) => [...rows, optimistic]);
    try {
      const created = await crmCreate<Record<string, unknown>>('tasks', taskToCrmCreateBody(task));
      const mapped = mapTaskApiToCrm(created);
      setData((rows) => rows.map((t) => (t.id === tempId ? mapped : t)));
    } catch (e) {
      setData(prev);
      throw e;
    }
  }, []);

  const removeTask = useCallback(async (id: string) => {
    const prev = structuredClone(dataRef.current);
    setData((rows) => rows.filter((t) => t.id !== id));
    try {
      await crmDelete('tasks', id);
    } catch (e) {
      setData(prev);
      throw e;
    }
  }, []);

  const mutate = useMemo(
    () => ({ addTask, patchTask, removeTask }) satisfies UseCrmTasksMutate,
    [addTask, patchTask, removeTask]
  );

  return { data, isLoading, error, mutate, refetch };
}
